import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";
import { EASE } from "@/components/Reveal";

const LINKS = [
  ["approach", "Approach"],
  ["services", "What we do"],
  ["process", "Process"],
  ["pricing", "Pricing"],
  ["faqs", "FAQs"],
  ["contact", "Contact"],
];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToSection(`#${id}`);
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "border-b border-[#201e32]/10 bg-[#f3f0e8]/85 text-[#201e32] backdrop-blur-xl"
            : "border-b border-transparent bg-transparent text-[#f3f0e8]"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
          <button
            data-testid="nav-logo"
            onClick={() => scrollToSection(0)}
            className="group flex items-baseline gap-1 font-display text-xl font-bold tracking-tight"
          >
            elavate
            <span className={`inline-block h-2 w-2 rounded-full transition-[transform,background-color] duration-300 group-hover:scale-150 ${scrolled ? "bg-[#7650c7]" : "bg-[#a277fa]"}`} />
          </button>

          <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
            {LINKS.map(([id, label], i) => (
              <button
                key={id}
                data-testid={`nav-link-${id}`}
                onClick={() => go(id)}
                className={`group relative font-mono text-[11px] uppercase tracking-[0.18em] transition-colors ${
                  scrolled ? "text-[#201e32]/70 hover:text-[#201e32]" : "text-white/60 hover:text-white"
                }`}
              >
                <span className={`mr-1 ${scrolled ? "text-[#7650c7]" : "text-[#a277fa]"}`}>0{i + 1}</span> {label}
                <span className={`absolute -bottom-1 left-0 h-px w-0 transition-[width] duration-300 group-hover:w-full ${scrolled ? "bg-[#7650c7]" : "bg-[#a277fa]"}`} />
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-cta-start"
              onClick={() => go("contact")}
              className={`group hidden items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-transform duration-300 hover:-translate-y-0.5 sm:inline-flex ${
                scrolled ? "bg-[#201e32] text-[#f3f0e8]" : "bg-[#f4d35e] text-[#201e32]"
              }`}
            >
              Start a conversation
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="nav-mobile-menu-button"
              onClick={() => setOpen(true)}
              className={`inline-flex h-10 w-10 items-center justify-center rounded-full border lg:hidden ${
                scrolled ? "border-[#201e32]/15" : "border-white/25"
              }`}
              aria-label="Open menu"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[80] flex flex-col bg-[#201e32]/97 px-6 py-6 backdrop-blur-xl"
          >
            <div className="flex items-center justify-between">
              <span className="font-display text-xl font-bold text-[#f3f0e8]">
                elavate<span className="text-[#a277fa]">.</span>
              </span>
              <button
                data-testid="mobile-menu-close-button"
                onClick={() => setOpen(false)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-[#f3f0e8]"
                aria-label="Close menu"
              >
                <X size={18} />
              </button>
            </div>
            <nav className="mt-14 flex flex-col gap-2" aria-label="Mobile">
              {LINKS.map(([id, label], i) => (
                <motion.button
                  key={id}
                  data-testid={`mobile-nav-link-${id}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: EASE }}
                  onClick={() => go(id)}
                  className="flex items-baseline gap-4 border-b border-white/10 py-4 text-left"
                >
                  <span className="font-mono text-xs text-[#a277fa]">0{i + 1}</span>
                  <span className="font-display text-3xl font-bold text-[#f3f0e8]">{label}</span>
                </motion.button>
              ))}
            </nav>
            <p className="mt-auto font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">
              Thoughtful websites for real people
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
